import solutionsJson from '../content/solutions.json';

/**
 * Marketplace services data
 * Contains all service offerings displayed in the marketplace section.
 *
 * The tiles themselves are edited through the CMS
 * (src/content/solutions.json → services, see public/admin/config.yml); this
 * module only types and exposes them.
 */

/**
 * @typedef {Object} MarketplaceService
 * @property {string} category - Service category (e.g., 'SaaS', 'Professional Services')
 * @property {string} title - Service title
 * @property {string} description - Service description
 * @property {string} buttonText - CTA button text
 * @property {string} link - External link to service details
 * @property {string} [badgeUrl] - Optional badge image URL to display in top-right corner
 * @property {string} [badgeAlt] - Alt text for badge image
 * @property {boolean} [comingSoon] - If true, button will be disabled with "Coming Soon" styling
 * @property {{text: string, link: string, icon?: 'person'|'login'|'open'}} [actionButton] - Optional secondary action shown as a small outlined button in the tile's top-right corner (EllaFin: "Sign In" → app.ellafin.ai)
 */

/**
 * Array of marketplace service offerings.
 *
 * Ordered to display in 2 rows of 3 (3-column grid on desktop), matching the
 * "Solution Tiles Update" deck. Order is whatever the CMS list order is.
 *
 * Interim links worth knowing about when editing:
 * - HubLife points at its Marketplace listing until the HubLife page is built.
 * - Amazon Connect points at AWS's own product page until the RSMC page
 *   exists; the deck had this tile as "Coming Soon" in the interim.
 * - HubLife uses the white "Powered by AWS" badge because the default artwork
 *   is too dark to read on the card.
 *
 * @type {MarketplaceService[]}
 */
export const MARKETPLACE_SERVICES = solutionsJson.services;

// --- Retired tiles ---------------------------------------------------------
// Dropped in the "Solution Tiles Update" deck. Kept here (rather than in git
// history alone) because the /aws page they linked to is still live and these
// were its only entry points from the homepage.
//
// {
//   category: 'Professional Services',
//   title: 'SMB',
//   description: 'Modernization Consulting for a Small & Medium Sized Business',
//   buttonText: 'Learn More',
//   link: '/aws',
// },
// {
//   category: 'Professional Services',
//   title: 'FinOps Consulting',
//   description:
//     'AWS Cost Visibility, Automated Savings Actions & Financial Accountability',
//   buttonText: 'Learn More',
//   link: 'https://aws.amazon.com/marketplace/pp/prodview-imjbjkbyylkko?sr=0-3&ref_=beagle&applicationId=AWSMPContessa',
// },
// {
//   category: 'Professional Services',
//   title: 'Healthcare & Life Sciences',
//   description:
//     'Modernization consulting for HealthTech, Biotech & Research Organizations',
//   buttonText: 'Learn More',
//   link: '/aws',
// },
// {
//   category: 'Professional Services',
//   title: 'Public Sector',
//   description:
//     'Modernization consulting for Nonprofits, Credit Unions, Higher Ed, State & Local Government',
//   buttonText: 'Learn More',
//   link: '/aws',
// },

/**
 * Get services by category
 * @param {string} category - The category to filter by
 * @returns {MarketplaceService[]} Filtered services
 */
export function getServicesByCategory(category) {
  return MARKETPLACE_SERVICES.filter(
    (service) => service.category === category
  );
}

/**
 * Get all unique service categories
 * @returns {string[]} Array of unique categories
 */
export function getServiceCategories() {
  return [...new Set(MARKETPLACE_SERVICES.map((service) => service.category))];
}
